// Settings → General: the admin's switch for correcting Day Close paid-outs in
// the Expense ledger (F-CTRL-N79). Off until an admin turns it on; the server
// stores only a boolean and refuses a non-admin write, so the card is
// admin-only as well.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'

export const EDIT_AUTO_LINKED_SETTING_KEY = 'expenses.edit_auto_linked'

export function EditAutoLinkedExpensesSettingCard() {
  const all = useAllSettings()
  const stored = all?.[EDIT_AUTO_LINKED_SETTING_KEY] === true
  const qc = useQueryClient()
  const { toast } = useToast()
  const [value, setValue] = useState(stored)
  useEffect(() => setValue(stored), [stored])

  const save = useMutation({
    mutationFn: async (next: boolean) => {
      const res = await apiClient.updateSetting(EDIT_AUTO_LINKED_SETTING_KEY, next)
      if (!res.success) throw new Error(res.message || 'Could not save')
      return res
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] })
      qc.invalidateQueries({ queryKey: ['expenses'] })
      toast({ title: 'Settings saved', description: 'Day Close paid-out corrections updated.' })
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  })

  if (getStoredUserRole() !== 'admin') return null

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Correcting Day Close paid-outs</CardTitle>
        <CardDescription>
          Cash paid out at Day Close lands in the Expense ledger automatically and is normally read-only there.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-start gap-3">
          <Switch id="edit-auto-linked" checked={value} onCheckedChange={setValue} />
          <div className="space-y-0.5">
            <Label htmlFor="edit-auto-linked" className="text-base">
              Let admins correct paid-outs in the Expense ledger
            </Label>
            <p className="text-sm text-muted-foreground">
              An admin can move a paid-out to another category on any day, and change its amount while that business
              day is still open (the drawer changes with it). Every change is kept in the Day Close log.
            </p>
          </div>
        </div>
        <Button className="h-11 px-6" disabled={save.isPending || value === stored} onClick={() => save.mutate(value)}>
          Save
        </Button>
      </CardContent>
    </Card>
  )
}
